import {FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from "@mui/material";
import { SelectorParameters } from "../../types/SelectorParameters";
import { BaseAssetDto } from "../../../../shared/types/BaseAssetDto";
import { LocalStorageConstants } from "../../../../shared/constants/LocalStorageConstants";
import type { MarketDataDto } from "../../../../shared/types/MarketDataDto";
import { getBaseAssetById, isAnyBaseAssetAvailable } from "../../utils/MarketDataUtils";
import {
    useAppSidePanelContextProvider
} from "../../../../shared/contexts/app-side-panel-context-provider/components/AppSidePanelContextProvider";
import {NumberFormatOptions, numberFormatters} from "../../../../screens/shared/utils/numberFormatters";

export function UnderlyingAssetSelector({
    marketDataDto,
    setValue,
    fetchMarketData,
}: SelectorParameters<BaseAssetDto>) {
    const {
        selectedPortfolio,
    } = useAppSidePanelContextProvider();
    
    const currentBaseAsset = getBaseAssetById(marketDataDto, marketDataDto?.BaseAssetSymbol);
    const currentBaseAssetName = currentBaseAsset?.BaseAsset;

    function handleSelection(eventArg: SelectChangeEvent<string>) {
        const selectedBaseAssetName = eventArg.target.value as string;
        const selectedBaseAsset = getBaseAssetByName(marketDataDto, selectedBaseAssetName);
        const selectedBaseAssetSymbol = selectedBaseAsset?.Symbol;

        setValue(selectedBaseAsset);
        localStorage.setItem(LocalStorageConstants.SelectedBaseAssetSymbol, selectedBaseAssetSymbol ?? '');
        localStorage.setItem(LocalStorageConstants.SelectedPortfolioName, '');
        fetchMarketData(selectedBaseAssetSymbol ?? null, marketDataDto?.ExpireAt?.toString() ?? null);
    }

    const primaryColour = "#FACC2D";
    const secondaryColour = "#FACC2D";
    const secondaryHoverColour = "#FFFFFF";
    
    const getSelectedAssetCurrentPrice = (): number => {
        const baseAssetPrice = marketDataDto?.BaseAsset.Price ?? 0;
        
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null)
            return baseAssetPrice;
        
        const portfolioSettings = selectedPortfolio.portfolioSettings;
        
        return portfolioSettings.useCustomPrice
            ? portfolioSettings.customPrice
            : baseAssetPrice;
    }
    
    return (
        <FormControl variant="standard" sx={{ width: 155 }}>
            <InputLabel
                sx={{
                    color: secondaryColour,
                    "&.Mui-focused": {
                        color: secondaryHoverColour
                    }
                }}
                id="base-asset-label"
            >
                <b>Base assets</b> {getSelectControlLabelExtension(marketDataDto)}
            </InputLabel>
            <Select
                sx={{
                    color: primaryColour,
                    '&:before': {
                        borderColor: secondaryColour,
                    },
                    '&:after': {
                        borderColor: secondaryHoverColour,
                    },
                    '&:not(.Mui-disabled):hover::before': {
                        borderColor: secondaryColour,
                    },
                    '.MuiSvgIcon-root': {
                        fill: `${secondaryColour} !important`,
                    }
                }}
                labelId="base-asset-label"
                value={currentBaseAssetName ?? ''}
                onChange={handleSelection}
                size="small"
                {...(!isAnyBaseAssetAvailable(marketDataDto)
                    ? { disabled: true }
                    : {})}
            >
                {marketDataDto?.AvailableBaseAssets
                    .map((baseAssetDto) => (
                        <MenuItem
                            key={baseAssetDto.BaseAsset}
                            value={baseAssetDto.BaseAsset}
                        >
                            {getSelectControlMenuItemValue(baseAssetDto.BaseAsset, currentBaseAssetName, getSelectedAssetCurrentPrice())}
                        </MenuItem>
                    ))}
            </Select>
        </FormControl>
    )

    function getBaseAssetByName(
        marketDataDto: MarketDataDto | undefined,
        name: string
    ): BaseAssetDto | null {
        return marketDataDto?.AvailableBaseAssets
            .find((x) => x.BaseAsset === name) ?? null;
    }
}

function getSelectControlLabelExtension(
    marketDataDto: MarketDataDto | undefined
): string | null {
    return isAnyBaseAssetAvailable(marketDataDto)
        ? `(${marketDataDto?.AvailableBaseAssets.length ?? 0} available)`
        : null;
}

function getSelectControlMenuItemValue(
    baseAssetName: string,
    currentBaseAssetName: string | undefined,
    underlyingPrice: number | null
): string {

    
    const priceToDisplay = underlyingPrice !== null
        ? `${numberFormatters.format0(underlyingPrice, numberFormatters.hideGroupDelimiterFormatOptions)}`
        : "N/A"
    
    return baseAssetName === currentBaseAssetName
        ? `${baseAssetName}: ${priceToDisplay}`
        : baseAssetName;
}